import { supabase } from '../supabase';
import { UserProfile } from '../../types';

export interface SystemNotification {
  id: string;
  title: string;
  body: string;
  segment_type: string;
  url?: string;
  metadata?: Record<string, any>;
  recipient_ids: string[];
  created_at: string;
  updated_at: string;
  user_notification_status?: UserNotificationStatus;
}

export interface UserNotificationStatus {
  notification_id: string;
  user_id: string;
  read: boolean;
  read_at?: string;
  created_at: string;
}

export default class NotificationService {
  // Buscar todas as notificações de um usuário
  async getUserNotifications(userId: string): Promise<SystemNotification[]> {
    const { data, error } = await supabase
      .from('system_notifications')
      .select('*, user_notification_status!inner(*)')
      .contains('recipient_ids', [userId])
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar notificações:', error);
      throw error;
    }

    return data || [];
  }

  // Buscar notificações não lidas de um usuário
  async getUnreadNotifications(userId: string, limit?: number): Promise<SystemNotification[]> {
    let query = supabase
      .from('system_notifications')
      .select('*, user_notification_status!inner(*)')
      .contains('recipient_ids', [userId])
      .eq('user_notification_status.user_id', userId)
      .eq('user_notification_status.read', false)
      .order('created_at', { ascending: false });

    // Se um limite foi especificado, aplicá-lo
    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Erro ao buscar notificações não lidas:', error);
      throw error;
    }

    return data || [];
  }

  // Marcar uma notificação como lida
  async markAsRead(notificationId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('user_notification_status')
      .update({
        read: true,
        read_at: new Date().toISOString()
      })
      .eq('notification_id', notificationId)
      .eq('user_id', userId);

    if (error) {
      console.error('Erro ao marcar notificação como lida:', error);
      throw error;
    }
  }

  // Marcar todas as notificações do usuário como lidas
  async markAllAsRead(userId: string): Promise<void> {
    const { error } = await supabase
      .from('user_notification_status')
      .update({
        read: true,
        read_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .eq('read', false);

    if (error) {
      console.error('Erro ao marcar todas notificações como lidas:', error);
      throw error;
    }
  }

  // Criar uma nova notificação do sistema
  async createSystemNotification(
    title: string,
    body: string,
    segmentType: string,
    recipientIds: string[],
    url?: string,
    metadata?: Record<string, any>
  ): Promise<SystemNotification> {
    // Inserir a notificação
    const { data: notification, error: notificationError } = await supabase
      .from('system_notifications')
      .insert({
        title,
        body,
        segment_type: segmentType,
        url,
        metadata,
        recipient_ids: recipientIds
      })
      .select()
      .single();

    if (notificationError) {
      console.error('Erro ao criar notificação:', notificationError);
      throw notificationError;
    }

    // Criar registros de status para cada destinatário
    const statusRecords = recipientIds.map(userId => ({
      notification_id: notification.id,
      user_id: userId,
      read: false
    }));

    const { error: statusError } = await supabase
      .from('user_notification_status')
      .insert(statusRecords);

    if (statusError) {
      console.error('Erro ao criar status das notificações:', statusError);
      throw statusError;
    }

    return notification;
  }

  // Excluir uma notificação e seus status
  async deleteNotification(notificationId: string): Promise<void> {
    // Os status serão excluídos automaticamente devido à restrição ON DELETE CASCADE
    const { error } = await supabase
      .from('system_notifications')
      .delete()
      .eq('id', notificationId);

    if (error) {
      console.error('Erro ao excluir notificação:', error);
      throw error;
    }
  }
} 